import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import { Table, TableBody, Typography, TableContainer, TableHead, TableRow, Paper, Stack, Pagination, Box, Chip, FormControl, Select, MenuItem, Button, TextField, Autocomplete } from '@mui/material';
import SearchInput from '../../../../utils/searchInput';
import { dayStartISO, dayEndISO } from '../../../../utils/dateRange';
import { setMainHeading } from '../../../../redux/modules/mainHeading/slice';
import { getLabOrderRequest } from '../../../../redux/modules/laboratory/labOrder/slice';
import { getLabTestRequest } from '../../../../redux/modules/laboratory/labTest/slice';
import { getLabTestPanelRequest } from '../../../../redux/modules/laboratory/labTestPanel/slice';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: { backgroundColor: theme.palette.primary.main, color: theme.palette.common.white },
    [`&.${tableCellClasses.body}`]: { fontSize: 14 },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover },
    '&:last-child td, &:last-child th': { border: 0 },
}));

const statusColors = {
    ordered: 'info', collected: 'warning', received: 'secondary', processing: 'primary',
    resulted: 'success', verified: 'success', released: 'default', cancelled: 'error'
};

let limit = 10;

export default function LabResultWorklist() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const basePath = location.pathname.split('/laboratory')[0];
    let { getRes } = useSelector(state => state.labOrder);
    let { currentBranch } = useSelector(state => state.branch);
    const allTests = useSelector(state => state.labTest.getRes?.data);
    const allPanels = useSelector(state => state.labTestPanel.getRes?.data);
    // Filters + page seed from the URL so the Back button / browser back restores
    // this same view; they're written back to the URL below.
    const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || "");
    const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || "");
    const [priorityFilter, setPriorityFilter] = useState(searchParams.get('priority') || "");
    const [dateFrom, setDateFrom] = useState(searchParams.get('from') || '');
    const [dateTo, setDateTo] = useState(searchParams.get('to') || '');
    const [testPanelFilter, setTestPanelFilter] = useState(null);
    const [currentPage, setCurrentPage] = useState(Number(searchParams.get('page')) || 1);
    const hydrated = useRef(false);

    useEffect(() => {
        dispatch(setMainHeading("Result Entry"));
        dispatch(getLabTestRequest({ page: 1, limit: 500, searchQuery: '' }));
        dispatch(getLabTestPanelRequest({ page: 1, limit: 100, searchQuery: '' }));
    }, [dispatch]);

    // Test/Panel filter — combined searchable options (orderable panels + tests),
    // resolved to the active id by kind. Mirrors the Lab Orders list filter.
    const testPanelOptions = [
        ...(allPanels || []).filter(p => p.isOrderable !== false).map(p => ({ kind: 'panel', _id: p._id, code: p.panelCode, name: p.name })),
        ...(allTests || []).filter(t => t.isOrderable !== false).map(t => ({ kind: 'test', _id: t._id, code: t.testCode, name: t.name })),
    ];
    const activeTestId = testPanelFilter?.kind === 'test' ? testPanelFilter._id : undefined;
    const activePanelId = testPanelFilter?.kind === 'panel' ? testPanelFilter._id : undefined;

    // Restore the Test/Panel filter object from the URL once the option lists load
    // (the URL only carries the id). `hydrated` gates the URL-writer so it can't
    // wipe the test/panel param before it's been reconstructed.
    useEffect(() => {
        const t = searchParams.get('test');
        const pn = searchParams.get('panel');
        if (!t && !pn) { hydrated.current = true; return; }
        if (t && allTests?.length) {
            const o = allTests.find(x => x._id === t);
            if (o) setTestPanelFilter({ kind: 'test', _id: o._id, code: o.testCode, name: o.name });
            hydrated.current = true;
        } else if (pn && allPanels?.length) {
            const o = allPanels.find(x => x._id === pn);
            if (o) setTestPanelFilter({ kind: 'panel', _id: o._id, code: o.panelCode, name: o.name });
            hydrated.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allTests, allPanels]);

    // Persist filters + page to the URL (replace, so keystrokes don't stack history).
    useEffect(() => {
        if (!hydrated.current) return;
        const p = {};
        if (searchQuery) p.q = searchQuery;
        if (statusFilter) p.status = statusFilter;
        if (priorityFilter) p.priority = priorityFilter;
        if (dateFrom) p.from = dateFrom;
        if (dateTo) p.to = dateTo;
        if (currentPage > 1) p.page = String(currentPage);
        if (activeTestId) p.test = activeTestId;
        if (activePanelId) p.panel = activePanelId;
        setSearchParams(p, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQuery, statusFilter, priorityFilter, dateFrom, dateTo, currentPage, testPanelFilter]);

    useEffect(() => {
        if (!currentBranch) return;
        // Show orders that need results (received, processing, resulted for review)
        dispatch(getLabOrderRequest({ page: currentPage, limit, searchQuery, status: statusFilter, priority: priorityFilter, dateFrom: dayStartISO(dateFrom), dateTo: dayEndISO(dateTo), testId: activeTestId, panelId: activePanelId }));
    }, [dispatch, currentBranch, currentPage, searchQuery, statusFilter, priorityFilter, dateFrom, dateTo, testPanelFilter]);

    const pageCount = getRes?.dataCount > limit ? Math.ceil(getRes.dataCount / limit) : 0;

    return (
        <>
            <Stack mb={3} mt={4} direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" flexWrap="wrap">
                <SearchInput defaultValue={searchQuery} setSearchQuery={(v) => { setSearchQuery(v); setCurrentPage(1); }} />
                <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                    <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }}
                        value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setCurrentPage(1); }}
                        sx={{ minWidth: 140 }} />
                    <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }}
                        value={dateTo} onChange={(e) => { setDateTo(e.target.value); setCurrentPage(1); }}
                        sx={{ minWidth: 140 }} />
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <Select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }} displayEmpty>
                            <MenuItem value="">All Status</MenuItem>
                            {['received', 'processing', 'resulted', 'verified'].map(s => (
                                <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                        <Select value={priorityFilter} onChange={(e) => { setPriorityFilter(e.target.value); setCurrentPage(1); }} displayEmpty>
                            <MenuItem value="">All Priority</MenuItem>
                            {['routine', 'urgent', 'stat'].map(p => (
                                <MenuItem key={p} value={p} sx={{ textTransform: 'capitalize' }}>{p}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Autocomplete
                        size="small" sx={{ minWidth: 220 }}
                        options={testPanelOptions}
                        value={testPanelFilter}
                        onChange={(e, v) => { setTestPanelFilter(v); setCurrentPage(1); }}
                        getOptionLabel={(o) => `${o.code} - ${o.name}${o.kind === 'panel' ? ' (Panel)' : ''}`}
                        isOptionEqualToValue={(o, v) => o.kind === v.kind && o._id === v._id}
                        renderInput={(params) => <TextField {...params} label="Test / Panel" />}
                        renderOption={(props, o) => (
                            <li {...props} key={`${o.kind}-${o._id}`}>
                                {o.code} - {o.name}
                                {o.kind === 'panel' && <Chip label="Panel" size="small" variant="outlined" sx={{ ml: 1, height: 18 }} />}
                            </li>
                        )}
                    />
                </Stack>
            </Stack>
            <Box sx={{ overflowX: { xs: 'auto', md: 'visible' }, mx: { xs: -2, sm: 0 }, px: { xs: 2, sm: 0 } }}>
                <TableContainer component={Paper} className="tableShadow">
                    <Table sx={{ minWidth: { xs: 400, md: 700 } }} className="commonTable">
                        <TableHead>
                            <TableRow>
                                <StyledTableCell>Accession</StyledTableCell>
                                <StyledTableCell>Patient</StyledTableCell>
                                <StyledTableCell>Tests</StyledTableCell>
                                <StyledTableCell>Status</StyledTableCell>
                                <StyledTableCell>Date</StyledTableCell>
                                <StyledTableCell align="right">Action</StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {getRes?.data?.length > 0 ? getRes.data.map(order => (
                                <StyledTableRow key={order._id}>
                                    <StyledTableCell sx={{ fontWeight: 600 }}>{order.accessionNumber || order.labOrderId || '-'}</StyledTableCell>
                                    <StyledTableCell>
                                        {order.patientId ? `${order.patientId.fName || ''} ${order.patientId.lName || ''}`.trim() : '-'}
                                        {order.patientId?.mrn && <Typography variant="caption" display="block" color="text.secondary">{order.patientId.mrn}</Typography>}
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {order.tests?.slice(0, 3).map((t, i) => (
                                                <Chip key={i} label={t.testId?.testCode || '?'} size="small" variant="outlined" />
                                            ))}
                                            {order.tests?.length > 3 && <Chip label={`+${order.tests.length - 3}`} size="small" />}
                                        </Box>
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <Chip label={order.status} size="small" color={statusColors[order.status] || 'default'} sx={{ textTransform: 'capitalize' }} />
                                    </StyledTableCell>
                                    <StyledTableCell>{order.orderedAt ? new Date(order.orderedAt).toLocaleDateString() : '-'}</StyledTableCell>
                                    <StyledTableCell align="right">
                                        <Button size="small" variant="outlined" onClick={() => navigate(`${basePath}/laboratory/results/entry/${order._id}`, { state: { from: location.pathname + location.search } })}>
                                            {['received', 'processing'].includes(order.status) ? 'Enter Results' : 'View Results'}
                                        </Button>
                                    </StyledTableCell>
                                </StyledTableRow>
                            )) : (
                                <StyledTableRow>
                                    <StyledTableCell colSpan={6}><Typography>No orders found</Typography></StyledTableCell>
                                </StyledTableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
            {pageCount > 0 && <Pagination count={pageCount} page={currentPage} onChange={(e, v) => setCurrentPage(v)} />}
        </>
    );
}
